import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentAccount } from '@mysten/dapp-kit';
import { useAGUBalance, useStakingPool, useGovernanceConfig, useAGUTreasury } from '../sui/hooks';
import { AGU_DECIMALS, PACKAGE_ID, explorerUrl } from '../sui/constants';

export default function Overview() {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const { balance } = useAGUBalance();
  const { poolInfo, isLoading: poolLoading } = useStakingPool();
  const { config } = useGovernanceConfig();
  const { balance: treasuryBalance } = useAGUTreasury();

  const totalStaked = Number(BigInt(poolInfo.totalStaked)) / 10 ** AGU_DECIMALS;
  const rewardPool = Number(BigInt(poolInfo.rewardBalance)) / 10 ** AGU_DECIMALS;

  return (
    <div className="agu-container">
      <section className="agu-hero">
        <div className="agu-hero-content">
          <span className="agu-pill">{t('hero.badge')}</span>
          <h1>{t('hero.title')}</h1>
          <p className="agu-subtitle">{t('hero.subtitle')}</p>
          <p className="agu-description">{t('hero.description')}</p>
          <div className="agu-hero-actions">
            <button className="agu-button">✨ {t('buttons.launch')}</button>
            <button className="agu-button secondary">📋 {t('buttons.learn')}</button>
          </div>
        </div>

        <div className="agu-hero-glow"></div>
        <div className="agu-hero-glow-2"></div>
      </section>

      <div className="agu-kpi-grid">
        <div className="agu-kpi-card">
          <span>Total Staked</span>
          <strong>{poolLoading ? '...' : `${totalStaked.toLocaleString()} AGU`}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Staking APY</span>
          <strong>{poolInfo.emissionRateBps > 0 ? `${poolInfo.emissionRateBps / 100}%` : '8%'}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Active Stakers</span>
          <strong>{poolInfo.stakeCount > 0 ? poolInfo.stakeCount.toLocaleString() : '—'}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>{account ? 'My Balance' : 'Reward Pool'}</span>
          <strong>{account ? `${balance.toLocaleString()} AGU` : `${rewardPool.toLocaleString()} AGU`}</strong>
        </div>
      </div>

      <section className="agu-section">
        <div className="agu-section-header">
          <h2>🌍 AGU Overview</h2>
          <p>AGU는 검증 가능한 판단을 시장에 남기기 위한 최소 신뢰 단위입니다.</p>
        </div>
        <div className="agu-grid">
          <div className="agu-card">
            <div className="agu-card-title">🧭 Identity</div>
            <p className="agu-body">
              AGU는 단순한 거버넌스 토큰이 아니라, 정확한 판단에 책임을 부여하는 신뢰 자본입니다.
            </p>
            <div className="agu-badge">검증 실패 시 슬래싱/소각</div>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">🔒 Design Philosophy</div>
            <p className="agu-body">
              보유량이 아니라 정확도와 책임이 권력을 결정합니다. 틀린 판단은 구조적으로 비용을 지불합니다.
            </p>
            <div className="agu-badge">Verified Decision Economy</div>
          </div>
        </div>
      </section>

      <div className="agu-grid">
        <div className="agu-card">
          <div className="agu-card-title">🪙 {t('info.token')}</div>
          <p className="agu-body">{t('info.tokenDesc')}</p>
          <div className="agu-badge">{t('info.supply')}: 1B AGU</div>
        </div>
        <div className="agu-card">
          <div className="agu-card-title">⚡ {t('info.utility')}</div>
          <ul className="agu-list">
            <li>
              <strong>{t('info.utility1')}</strong> — {t('info.utility1Desc')}
            </li>
            <li>
              <strong>{t('info.utility2')}</strong> — {t('info.utility2Desc')}
            </li>
            <li>
              <strong>{t('info.utility3')}</strong> — {t('info.utility3Desc')}
            </li>
          </ul>
        </div>
      </div>

      <section className="agu-section">
        <div className="agu-section-header">
          <h2>🧩 Token Utilities</h2>
        </div>
        <div className="agu-grid">
          <div className="agu-card">
            <div className="agu-card-title">🔒 Staking = 책임 기반 참여</div>
            <p className="agu-body">Agent/Research/Audit 참여 전 AGU 스테이킹이 필요합니다.</p>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">🔥 Verification Cost</div>
            <p className="agu-body">Research, Audit, Market 등록 등 핵심 행위에 AGU가 소모됩니다.</p>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">🧠 Accuracy Weighted Power</div>
            <p className="agu-body">정확도 이력에 따라 영향력이 가중됩니다.</p>
          </div>
        </div>
      </section>

      <section className="agu-section">
        <div className="agu-section-header">
          <h2>🚀 {t('vision.title')}</h2>
        </div>
        <div className="agu-grid">
          <div className="agu-card">
            <div className="agu-card-title">🤖 {t('vision.vision1')}</div>
            <p className="agu-body">{t('vision.vision1Desc')}</p>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">🌐 {t('vision.vision2')}</div>
            <p className="agu-body">{t('vision.vision2Desc')}</p>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">💎 {t('vision.vision3')}</div>
            <p className="agu-body">{t('vision.vision3Desc')}</p>
          </div>
        </div>
      </section>

      <section className="agu-section" id="roadmap">
        <div className="agu-section-header">
          <h2>📍 {t('roadmap.title')}</h2>
        </div>
        <div className="agu-timeline">
          {['q1', 'q2', 'q3', 'q4'].map((quarter) => {
            const features = t(`roadmap.${quarter}Features`, { returnObjects: true });
            const featuresList = Array.isArray(features) ? features : [];
            return (
              <div className="agu-timeline-item" key={quarter}>
                <div className="agu-timeline-dot"></div>
                <div className="agu-timeline-content">
                  <div className="agu-timeline-phase">{t(`roadmap.${quarter}`)}</div>
                  <h3>{t(`roadmap.${quarter}Title`)}</h3>
                  <p className="agu-muted">{t(`roadmap.${quarter}Desc`)}</p>
                  <ul className="agu-list">
                    {featuresList.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="agu-section agu-whitepaper-section">
        <div className="agu-section-header">
          <h2>📖 {t('whitepaper.title')}</h2>
          <p>{t('whitepaper.subtitle')}</p>
        </div>

        <div className="agu-whitepaper-grid">
          <div className="agu-whitepaper-card">
            <h3>{t('whitepaper.toc')}</h3>
            <div className="agu-toc">
              <div className="agu-toc-item">✓ {t('whitepaper.summary')}</div>
              <div className="agu-toc-item">✓ {t('whitepaper.architecture')}</div>
              <div className="agu-toc-item">✓ {t('whitepaper.security')}</div>
              <div className="agu-toc-item">✓ {t('info.tokenomics')}</div>
            </div>
          </div>

          <div className="agu-whitepaper-card">
            <h3>{t('whitepaper.summary')}</h3>
            <p>{t('whitepaper.summaryText')}</p>
          </div>

          <div className="agu-whitepaper-card">
            <h3>{t('whitepaper.download')}</h3>
            <div className="agu-downloads">
              <div className="agu-download-card">
                <div>
                  <div className="agu-download-title">📘 {t('whitepaper.downloadPdf')}</div>
                  <div className="agu-download-meta">{t('whitepaper.downloadSize')}</div>
                </div>
                <button className="agu-button" style={{ padding: '8px 16px', fontSize: '12px' }}>
                  ↓
                </button>
              </div>
            </div>
            <span className="agu-status-pill">📋 Coming Soon</span>
          </div>
        </div>
      </section>

      <section className="agu-section">
        <div className="agu-cta">
          <h2>Ready to Join the Future?</h2>
          <p>Start your journey with AGU today. Connect your SUI wallet and participate in the AI Agent economy.</p>
          <div className="agu-cta-actions">
            <button className="agu-button">🚀 {t('buttons.launch')}</button>
            <button className="agu-button secondary">💬 Join Community</button>
          </div>
          <p className="agu-muted" style={{ marginTop: 16, fontSize: 12 }}>
            Contract: <a href={explorerUrl('object', PACKAGE_ID)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--agu-accent)' }}>{PACKAGE_ID.slice(0, 10)}...{PACKAGE_ID.slice(-8)} ↗</a> — SUI Testnet
          </p>
        </div>
      </section>
    </div>
  );
}
